import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { createAPIKey, listAPIKeys, revokeAPIKey, APIKey } from '../api/apikeys';

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-slate-50 transition-colors"
    >
      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
        {copied ? 'check' : 'content_copy'}
      </span>
      {copied ? 'Copied!' : label}
    </button>
  );
}

function NewKeyModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ key: string; keyPrefix: string } | null>(null);
  const apiUrl = window.location.origin;

  const submit = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await createAPIKey(name.trim());
      setResult({ key: res.key, keyPrefix: res.keyPrefix });
      onCreated();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
          <h2 className="text-base font-bold text-text-main">
            {result ? 'API Key Created' : 'New API Key'}
          </h2>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-slate-100">
            <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 20 }}>close</span>
          </button>
        </div>

        {result ? (
          /* ── Success state — show raw key once ── */
          <div className="p-6 space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-amber-600 mt-0.5" style={{ fontSize: 18 }}>warning</span>
                <p className="text-sm text-amber-800">
                  <strong>Copy this key now.</strong> It will not be shown again. Store it in a secrets manager (AWS Secrets Manager, Azure Key Vault, or similar).
                </p>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-text-faint">
                Your API Key
              </label>
              <div className="flex items-center gap-2 rounded-xl border border-border-subtle bg-slate-50 px-4 py-3">
                <code className="flex-1 break-all font-mono text-xs text-text-main">{result.key}</code>
                <CopyButton text={result.key} />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-text-faint">
                SDK Quick Start
              </label>
              <div className="relative rounded-xl border border-border-subtle bg-slate-900 p-4">
                <CopyButton
                  text={`import { IDAClient } from '@myida/sdk';\n\nconst client = new IDAClient({\n  apiUrl: '${apiUrl}',\n  apiKey: '${result.key}',\n});`}
                  label="Copy snippet"
                />
                <pre className="mt-2 overflow-x-auto font-mono text-xs text-slate-300">{`import { IDAClient } from '@myida/sdk';

const client = new IDAClient({
  apiUrl: '${apiUrl}',
  apiKey: '${result.key}',
});`}</pre>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
            >
              Done — I've saved the key
            </button>
          </div>
        ) : (
          /* ── Create form ── */
          <div className="p-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-main">
                Key Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submit()}
                placeholder="e.g. AgentSight production"
                maxLength={100}
                className="w-full rounded-xl border border-border-subtle px-4 py-2.5 text-sm text-text-main placeholder:text-text-faint focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
              {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
            </div>

            <p className="text-xs text-text-faint">
              The key will have access to all API endpoints on behalf of your account. You can revoke it at any time.
            </p>

            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 rounded-xl border border-border-subtle px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={loading || !name.trim()}
                className="flex-1 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Creating…' : 'Create Key'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<APIKey | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await listAPIKeys();
      setKeys(data);
    } catch {
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    const id = revokeTarget.id;
    setRevoking(id);
    try {
      await revokeAPIKey(id);
      setKeys(prev => prev.filter(k => k.id !== id));
      setRevokeTarget(null);
    } catch {
      setError('Failed to revoke key');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-main">Settings</h1>
        <p className="mt-1 text-sm text-text-secondary">Manage your account and API access.</p>
      </div>

      {/* Account card */}
      <div className="mb-6 rounded-2xl border border-border-subtle bg-white p-6">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-widest text-text-faint">Account</h2>
        <div className="flex items-center gap-4">
          <div className="flex size-12 items-center justify-center rounded-full bg-indigo-100">
            <span className="text-lg font-bold text-indigo-700">
              {user?.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) ?? '??'}
            </span>
          </div>
          {/* min-w-0 lets this flex child shrink; break-all keeps a long
              address inside the card instead of pushing the row wider. */}
          <div className="min-w-0">
            <p className="truncate font-semibold text-text-main" title={user?.name}>{user?.name}</p>
            <p className="break-all text-sm text-text-secondary" title={user?.email}>{user?.email}</p>
            <span className="mt-0.5 inline-block rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700">
              {user?.role}
            </span>
          </div>
        </div>
      </div>

      {/* API Keys card */}
      <div className="rounded-2xl border border-border-subtle bg-white">
        <div className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
          <div>
            <h2 className="text-sm font-bold text-text-main">API Keys</h2>
            <p className="mt-0.5 text-xs text-text-faint">
              Use these keys with the <code className="rounded bg-slate-100 px-1 font-mono text-[11px]">@myida/sdk</code> or any HTTP client.
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
            New Key
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="size-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
          </div>
        ) : keys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <span className="material-symbols-outlined mb-3 text-text-faint" style={{ fontSize: 40 }}>key</span>
            <p className="text-sm font-medium text-text-main">No API keys yet</p>
            <p className="mt-1 text-xs text-text-faint">Create a key to start using the SDK.</p>
          </div>
        ) : (
          <div className="divide-y divide-border-subtle">
            {keys.map(k => (
              <div key={k.id} className="flex items-center gap-4 px-6 py-4">
                <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 20 }}>vpn_key</span>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-semibold text-text-main">{k.name}</p>
                  <div className="mt-0.5 flex items-center gap-3">
                    <code className="font-mono text-xs text-text-faint">{k.keyPrefix}…</code>
                    <span className="text-xs text-text-faint">
                      {k.lastUsedAt ? `Used ${timeAgo(k.lastUsedAt)}` : 'Never used'}
                    </span>
                    {k.expiresAt && (
                      <span className="text-xs text-amber-600">
                        Expires {new Date(k.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setRevokeTarget(k)}
                  disabled={revoking === k.id}
                  className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                >
                  {revoking === k.id ? (
                    <span className="size-3 animate-spin rounded-full border border-red-400 border-t-transparent" />
                  ) : (
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
                  )}
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}

        {/* SDK usage hint */}
        {keys.length > 0 && (
          <div className="border-t border-border-subtle bg-slate-50 px-6 py-4 rounded-b-2xl">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-faint">SDK Usage</p>
            <pre className="overflow-x-auto rounded-xl bg-slate-900 p-4 font-mono text-xs text-slate-300">{`import { IDAClient } from '@myida/sdk';

const client = new IDAClient({
  apiUrl: '${window.location.origin}',
  apiKey: '<your-key-above>',
});`}</pre>
          </div>
        )}
      </div>

      {showModal && (
        <NewKeyModal
          onClose={() => setShowModal(false)}
          onCreated={load}
        />
      )}

      {/* Revoke confirmation — styled modal in place of window.confirm() */}
      {revokeTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => revoking ? null : setRevokeTarget(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-red-50">
                  <span className="material-symbols-outlined text-red-600" style={{ fontSize: 22 }}>warning</span>
                </div>
                <div className="flex-1">
                  <h2 className="text-base font-bold text-text-main">Revoke API key</h2>
                  <p className="mt-1 text-sm text-text-secondary">
                    Revoke <span className="font-semibold text-text-main">{revokeTarget.name}</span>? Any SDK
                    integrations using it will stop working immediately. This cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-border-subtle px-6 py-4">
              <button
                onClick={() => setRevokeTarget(null)}
                disabled={!!revoking}
                className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-main hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRevoke}
                disabled={!!revoking}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {revoking && (
                  <span className="size-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}
                {revoking ? 'Revoking…' : 'Revoke key'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
